import { css, useTheme } from '@emotion/react'
import { useState, useEffect, useMemo, useCallback } from 'react'
import * as React from 'react'
import createDecorator from 'final-form-calculate'
import { observer, inject } from 'mobx-react'
import { Form, Field } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import _ from 'lodash'

import { CircleNegative, CirclePlus } from '../../../assets/icons'
import {
  Button,
  Spacer,
  SVGWrapper,
  SliderWithValueUnderneath,
  FlexRow,
  Loading,
  Drawer,
  HelpIcon,
  TooltipText,
} from '../../../components'
import { numberToDollars } from '../../../utils/utils'
import { ButtonGroup } from '../../offboarding/confirmation/ConfirmationStyle'
import { BackButton, Row } from '../../../components/styled'
import { Page, Section } from './style'
import { schema } from './validate'
import {
  reduceValidationError,
  companyStocksAt100PercentForSale,
  withRouterProps,
} from '../../../utils'
import { DoughnutChart } from '../../shared'

import UnderstandingYourResults from '../understanding-your-results/UnderstandingYourResults'
import ChangeRetirementAge from '../considerations/shared-components/ChangeRetirementAge'
import WorkPartTimeAfterRetiring from '../considerations/shared-components/WorkPartTimeAfterRetiring'
import AddAnEmergencyFund from '../considerations/shared-components/AddAnEmergencyFund'
import WhereMoneyComesFrom from '../shared-components/WhereMoneyComesFrom'
import SellCompanyStock from '../considerations/shared-components/SellCompanyStock'

const Condition = ({ when, is, children }) => (
  <Field name={when} subscription={{ value: true }}>
    {({ input: { value } }) => (value === is ? children : null)}
  </Field>
)

function FinalizeChoices(props) {
  const {
    navigate,
    store: {
      projectedMonthlyRetirementInc,
      spouse,
      person,
      setInitialInfoForSpendDown,
      updatePerson,
      getPerson,
      updateSpouse,
      getSpouse,
      savePrimaryRetirementStatus,
      saveSpouseRetirementStatus,
      saveEmergencyFunds,
      config: { spendingMode },
      spendingModifiedCases,
      modifiedValues,
      spendingRecommendedCases,
      primaryRetirementStatus,
      spouseRetirementStatus,
      getPrimaryRetirementStatus,
      getSpouseRetirementStatus,
      processModifiedCase,
      totalPrimaryBalanceExcludingPensionAndAnnuity,
      getSpendingCaseById,
      emergencyFund,
      allSpendingBaseCases,
      spendingBaseCases,
      config,
    },
  } = props
  const [status, setState] = useState('loading')

  const handleFetch = useCallback(
    () => async () => {
      try {
        setState('loading')
        await setInitialInfoForSpendDown()
        setState('success')
      } catch (err) {
        setState('error')
        console.error(err)
      }
    },
    [setInitialInfoForSpendDown]
  )

  useEffect(() => {
    props.main.setSidebar(false)
    const fetchData = async () => {
      try {
        await getPrimaryRetirementStatus()
        if (person.maritalStatus && person.includeSpouse) {
          await getSpouseRetirementStatus()
        }
      } catch (err) {
        console.error(err)
      }
    }

    fetchData()
    if (_.isEmpty(person || spouse)) {
      handleFetch()
    } else {
      setState('success')
    }

    return () => {
      props.main.setSidebar(true)
    }
  }, [
    getPrimaryRetirementStatus,
    getSpouseRetirementStatus,
    handleFetch,
    person,
    props.main,
    spouse,
  ])

  const calculator = useMemo(
    () =>
      createDecorator(
        {
          field: 'emergencyFundInput',
          updates: {
            emergencyFundSlider: (ignoredValue, allValues) => {
              if (allValues.emergencyFundInput === undefined) return
              let number
              if (
                typeof allValues.emergencyFundInput === 'string' &&
                allValues.emergencyFundInput.includes(',')
              ) {
                number = allValues.emergencyFundInput.replace(',', '')
                number = parseInt(number)
              }
              if (
                !number &&
                (isNaN(allValues.emergencyFundInput) || allValues.emergencyFundInput === undefined)
              ) {
                return 0
              }
              if (
                number > allValues.maximumEmergencyFundInput ||
                allValues.emergencyFundInput > allValues.maximumEmergencyFundInput
              ) {
                return allValues.maximumEmergencyFundInput
              }
              return parseInt(number || allValues.emergencyFundInput)
            },
          },
        },
        {
          field: 'emergencyFundSlider',
          updates: {
            emergencyFundInput: (ignoredValue, allValues) => {
              return parseInt(allValues.emergencyFundSlider)
            },
          },
        }
      ),
    []
  )

  const mutators = {
    displaySecondPage: (args, state, utils) => {
      utils.changeValue(state, 'page', () => 2)
    },
    toggleBeforeTax: (args, state, utils) => {
      utils.changeValue(state, 'beforeTax', () => !state.formState.values.beforeTax)
    },
    toggleSeeWhy: (args, state, utils) => {
      utils.changeValue(state, 'seeWhy', () => !state.formState.values.seeWhy)
    },
    toggleWhereMoneyComesFrom: (args, state, utils) => {
      utils.changeValue(
        state,
        'whereMoneyComesFrom',
        () => !state.formState.values.whereMoneyComesFrom
      )
    },
  }

  const companyStocks = _.orderBy(
    _.get(allSpendingBaseCases, 'companyStocks', null).filter(
      companyStock => !companyStock.restricted
    ),
    [stock => stock.percentToSell, stock => stock.securityName.toLowerCase()],
    ['desc', 'asc']
  )

  const initialValues = {
    clientRetirement: _.get(person, 'retirementAge', null),
    spouseRetirement: _.get(spouse, 'retirementAge', null),
    canCoverExpenses: null,
    planningWorkPartTimeClient: Boolean(
      _.get(primaryRetirementStatus, 'planningWorkPartTime', null)
    ),
    durationStartingAgeClient: _.get(primaryRetirementStatus, 'durationStartingAge', null),
    durationStoppingAgeClient: _.get(primaryRetirementStatus, 'durationStoppingAge', null),
    clientIncome: _.get(primaryRetirementStatus, 'annualIncome', null),
    planningWorkPartTimeSpouse: Boolean(
      _.get(spouseRetirementStatus, 'planningWorkPartTime', null)
    ),
    durationStartingAgeSpouse: _.get(spouseRetirementStatus, 'durationStartingAge', null),
    durationStoppingAgeSpouse: _.get(spouseRetirementStatus, 'durationStoppingAge', null),
    spouseIncome: _.get(spouseRetirementStatus, 'annualIncome', null),
    emergencyFundInput: _.get(emergencyFund, 'amount', 0),
    emergencyFundSlider: _.get(emergencyFund, 'amount', 0),
    maximumEmergencyFundInput: totalPrimaryBalanceExcludingPensionAndAnnuity,
    clientLifeExpectancy: _.get(
      spendingModifiedCases,
      'averageMarketSpendingAdvice.primary.lifeExpectancyAge',
      null
    ),
    spouseLifeExpectancy: _.get(
      spendingModifiedCases,
      'averageMarketSpendingAdvice.spouse.lifeExpectancyAge',
      null
    ),
    page: 1,
    whereMoneyComesFrom: false,
    beforeTax: true,
    seeWhy: false,
    companyStocks: companyStocksAt100PercentForSale(companyStocks),
  }

  const onSubmit = async (values, form) => {
    const companyStocksToBeSold = values.companyStocks.filter(stock => stock.percentToSell >= 0)
    const formatCompanyStockForSubmit =
      companyStocksToBeSold.length > 0
        ? companyStocksToBeSold
            .map(stock => {
              return stock.aggregatedPositionIds.map(positionId => ({
                percentToSell: stock.percentToSell,
                positionId: positionId,
              }))
            })
            .flat()
        : values.companyStocks
            .map(stock => {
              return stock.aggregatedPositionIds.map(positionId => ({
                percentToSell: 100,
                positionId: positionId,
              }))
            })
            .flat()
    let spouseUpdates = {}
    if (person.includeSpouse) {
      spouseUpdates = {
        spouseExpectedLifespan: values.spouseLifeExpectancy
          ? parseInt(values.spouseLifeExpectancy)
          : null,
        spouse: {
          ..._.get(spendingModifiedCases, 'averageMarketSpendingAdvice.spouse', null),
          retAge: values.spouseRetirement ? parseInt(values.spouseRetirement) : null,
        },
        spouseRetirementWork: _.get(spouseRetirementStatus, 'id', null)
          ? {
              ...spouseRetirementStatus,
              durationStartingAge: values.durationStartingAgeSpouse
                ? values.durationStartingAgeSpouse
                : null,
              durationStoppingAge: values.durationStoppingAgeSpouse
                ? values.durationStoppingAgeSpouse
                : null,
              annualIncome: values.spouseIncome,
              planningWorkPartTime: values.planningWorkPartTimeSpouse ? 1 : 0,
            }
          : null,
      }
    }
    let primaryUpdates = {}
    if (values.clientRetirement) {
      primaryUpdates = {
        primary: {
          ..._.get(spendingModifiedCases, 'averageMarketSpendingAdvice.primary', null),
          retAge: values.clientRetirement ? parseInt(values.clientRetirement) : null,
        },
      }
    }
    await processModifiedCase({
      emergencyFund: values.emergencyFundInput ? values.emergencyFundInput : null,
      participantExpectedLifespan: values.clientLifeExpectancy
        ? parseInt(values.clientLifeExpectancy)
        : null,
      ...primaryUpdates,
      primaryRetirementWork: _.get(primaryRetirementStatus, 'id', null)
        ? {
            ...primaryRetirementStatus,
            durationStartingAge: values.durationStartingAgeClient
              ? values.durationStartingAgeClient
              : null,
            durationStoppingAge: values.durationStoppingAgeClient
              ? values.durationStoppingAgeClient
              : null,
            annualIncome: values.clientIncome,
            planningWorkPartTime: values.planningWorkPartTimeClient ? 1 : 0,
          }
        : null,
      ...spouseUpdates,
      companyStocks: formatCompanyStockForSubmit,
    })

    document.getElementById('main-app') && (document.getElementById('main-app').scrollTop = 0)
    form.mutators.displaySecondPage()
  }

  const handleImplementChanges = async () => {
    try {
      const changedRetirementAge =
        person.retirementAge !==
        _.get(spendingModifiedCases, 'averageMarketSpendingAdvice.primary.retAge', null)
      const changedRetirementStatus = !_.isEqual(
        primaryRetirementStatus,
        _.get(modifiedValues, 'primaryRetirementWork', null)
      )
      const changedEmergencyFund =
        _.get(modifiedValues, 'emergencyFund', 0) !== 0 &&
        _.get(modifiedValues, 'emergencyFund', 0) !==
          _.get(spendingRecommendedCases, 'spendingScenario.emergencyFund', 0)

      if (changedRetirementAge) {
        await updatePerson({
          retirementAge: _.get(
            spendingModifiedCases,
            'averageMarketSpendingAdvice.primary.retAge',
            null
          ),
        })
        await getPerson()
      }
      if (changedRetirementStatus) {
        const { annualIncome, durationStartingAge, durationStoppingAge, planningWorkPartTime } =
          modifiedValues.primaryRetirementWork
        const primaryRetirementUpdates = {
          ...primaryRetirementStatus,
          annualIncome,
          durationStartingAge,
          durationStoppingAge,
          planningWorkPartTime: planningWorkPartTime === 1,
        }

        await savePrimaryRetirementStatus(primaryRetirementUpdates)
      }

      if (changedEmergencyFund) {
        await saveEmergencyFunds({
          caseId: _.get(spendingModifiedCases, 'caseId', null),
          amount: _.get(modifiedValues, 'emergencyFund', 0),
        })
      }

      if (person.includeSpouse) {
        const spouseChangedRetirementAge =
          spouse.retirementAge !==
          _.get(spendingModifiedCases, 'averageMarketSpendingAdvice.spouse.retAge', null)
        const spouseChangedRetirementStatus = !_.isEqual(
          spouseRetirementStatus,
          _.get(modifiedValues, 'spouseRetirementWork', null)
        )

        if (spouseChangedRetirementAge) {
          await updateSpouse({
            retirementAge: _.get(
              spendingModifiedCases,
              'averageMarketSpendingAdvice.spouse.retAge',
              null
            ),
          })
          await getSpouse()
        }
        if (spouseChangedRetirementStatus) {
          const { annualIncome, durationStartingAge, durationStoppingAge, planningWorkPartTime } =
            modifiedValues.spouseRetirementWork
          const spouseRetirementUpdates = {
            ...spouseRetirementStatus,
            annualIncome,
            durationStartingAge,
            durationStoppingAge,
            planningWorkPartTime: planningWorkPartTime === 1,
          }

          await saveSpouseRetirementStatus(spouseRetirementUpdates)
        }
      }
      await getSpendingCaseById(spendingModifiedCases.caseId)
    } catch (err) {
      console.error(err)
    }
    navigate('/spending/implement/')
  }

  if (status === 'loading') {
    return <Loading />
  }

  if (status === 'error') {
    return (
      <div
        css={css`
          color: #e31e27;
          font-size: 14px;
          line-height: 1.5;
          text-align: center;
          padding: 0.25rem 0 0.75rem 0;
        `}>
        Something went wrong, please try again later.
      </div>
    )
  }

  const validate = values => {
    return schema
      .validate(values, { abortEarly: false })
      .then(() => {})
      .catch(err => reduceValidationError(err))
  }

  return (
    <Form
      keepDirtyOnReinitialize
      decorators={[calculator]}
      mutators={{ ...mutators, ...arrayMutators }}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validate}
      subscription={{ submitting: true, submitError: true, values: true }}
      render={({ form, handleSubmit, values }) => (
        <Page.Container
          css={css`
            color: rgb(58, 77, 87);
            max-width: 1000px;
          `}>
          <Page.SolidifyPlanHeading>Let's solidify that plan!</Page.SolidifyPlanHeading>
          <Spacer space='30px' />

          <Condition when='page' is={1}>
            {spendingMode ? (
              <>
                <Page.Text>
                  Great work! We hope you've taken the opportunity to play around with lots of
                  variables to "see what happens if..." Given you may have used a variety of ages
                  and amounts in different scenarios, we don't want to assume which choices are to
                  be part of the plan we'll help you implement. Let's take care of that here.
                </Page.Text>
                <Spacer space='20px' />
              </>
            ) : (
              <>
                <Page.Text>
                  Great work! We hope you've taken the opportunity to play around with lots of
                  variables to "see what happens if..." Although{' '}
                  <b>we won't be transacting on your finalized plan </b>
                  since you have not yet reached your retirement age, you may have used a variety of
                  ages and amounts in different scenarios and wish to review your choices and their
                  results here.
                  <br />
                  <br />
                  If not, selecting Home in the top menu will take you out of spend-down modeling
                  mode.
                </Page.Text>
                <Spacer space='20px' />
              </>
            )}
          </Condition>

          <Condition when='page' is={2}>
            <Page.SuccessBox>
              <Page.CongratsText>
                Congratulations! You can expect <br />
                monthly income of <br />
              </Page.CongratsText>
              <Spacer space='5px' />
              <Page.HeaderAmount>
                {numberToDollars(projectedMonthlyRetirementInc, true)}
              </Page.HeaderAmount>
              <Spacer space='5px' />
              <Page.BasedText>based on average investment returns and mortality.</Page.BasedText>
              <Spacer space='5px' />
              {_.get(spendingModifiedCases, 'spendingScenario.budget.dollarAmountDesired', null) <=
                _.get(spendingModifiedCases, 'averageMarketSpendingAdvice.monthlyIncome', null) && (
                <Page.CanAffordNeeds>
                  Well done, you are meeting your desired monthly spending total, which includes
                  your income need and want!
                </Page.CanAffordNeeds>
              )}
              <Spacer space='5px' />
              <Button
                type='button'
                secondary
                label='See why'
                css={css`
                  background-color: #4a606a9c;
                  color: #fff;
                `}
                onClick={form.mutators.toggleSeeWhy}
              />

              {_.get(spendingBaseCases, 'currentCompanyStockWeightPercentage', 0) > 0 && (
                <>
                  <Spacer space='10px' />

                  <Page.BasedText>
                    {' '}
                    Includes company stock in account with
                    <br /> {config.template.planName}{' '}
                    <HelpIcon tooltip={TooltipText.changeHowMuchInCompanyStock()} />
                  </Page.BasedText>

                  <Section>
                    <Spacer space='10px' />
                    <Section.SecondaryText marginLeft='46px'>
                      Percentage of your account in{' '}
                      {_.get(spendingBaseCases, 'companyStocks', [])
                        .map(stock => stock.securityName)
                        .join(', ')}
                    </Section.SecondaryText>
                    <Row>
                      <Section.SVGContainer>
                        <Section.SecondaryText>Current percentage</Section.SecondaryText>
                        <Spacer space='10px' />
                        <DoughnutChart
                          percent={_.get(
                            spendingBaseCases,
                            'currentCompanyStockWeightPercentage',
                            0
                          )}
                          height={130}
                        />
                      </Section.SVGContainer>
                      <Section.SVGContainer>
                        <Section.PrimaryText>if implemented</Section.PrimaryText>
                        <Spacer space='10px' />
                        <DoughnutChart
                          percent={_.get(
                            spendingModifiedCases,
                            'keptCompanyStockWeightPercentage',
                            0
                          )}
                          height={130}
                        />
                      </Section.SVGContainer>
                    </Row>
                  </Section>
                </>
              )}
            </Page.SuccessBox>

            <Drawer active={values.seeWhy} width='100%'>
              <UnderstandingYourResults handleCancel={form.mutators.toggleSeeWhy} />
            </Drawer>
            <Spacer space='8px' />

            <Page.DollarValue>
              The dollar value shown is after-tax. We estimate the values based on investing as
              recommended.
            </Page.DollarValue>
            <Spacer space='8px' />

            <FlexRow
              css={css`
                margin: 20px 0;
              `}
              onClick={form.mutators.toggleWhereMoneyComesFrom}>
              {values.whereMoneyComesFrom ? (
                <SVGWrapper svg={CircleNegative} fill='#7a8e96' size='large' />
              ) : (
                <SVGWrapper svg={CirclePlus} fill='#7a8e96' size='large' />
              )}
              <Page.TooltipLabel>See where the money comes from</Page.TooltipLabel>
            </FlexRow>

            {values.whereMoneyComesFrom && <WhereMoneyComesFrom />}

            <Spacer space='20px' />

            <Page.FeelGood>Feeling good about your plan?</Page.FeelGood>
            <Page.NotQuiteRight>
              If it's not quite right, continue editing your choices below till it is.
            </Page.NotQuiteRight>
            {!spendingMode && (
              <div
                css={css`
                  margin-top: 20px;
                `}>
                Note: If you wish to retain a copy of your choices, print this page Please be sure
                to expand both the area under "See where the money comes from" and all the options
                under other considerations.
              </div>
            )}

            <ButtonGroup
              css={css`
                margin: 20px 0;
              `}>
              <Page.CancelButton onClick={() => navigate('/overall')}>
                <div>Not ready</div>
                <div>no action | return to Overall Strategy</div>
              </Page.CancelButton>

              {spendingMode ? (
                <Page.PrimaryButton onClick={handleImplementChanges}>
                  <div>Ready to implement!</div>
                </Page.PrimaryButton>
              ) : (
                <Page.PrimaryButton onClick={() => navigate('/spending/implement/')}>
                  <div>Finished spend-down modeling</div>
                </Page.PrimaryButton>
              )}
            </ButtonGroup>
            <hr />
          </Condition>
          <Spacer space='20px' />
          <Page.BorderedCard>
            <div
              css={css`
                margin-top: 10px;
                font-size: 18px;
                line-height: 24px;
                color: #4a606a;
                position: relative;
              `}>
              <Page.Review>
                Review and make changes to ensure the variables below are part of your strategy as
                desired
              </Page.Review>

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  margin: 0 10px;
                `}>
                <ChangeLifeExpectancyAge
                  spendingRecommendedCases={spendingRecommendedCases}
                  spendingModifiedCases={spendingModifiedCases}
                  spouse={spouse}
                  person={person}
                />
                <ChangeRetirementAge />

                <WorkPartTimeAfterRetiring />
                {(_.get(emergencyFund, 'amount', 0) === 0 ||
                  _.get(emergencyFund, 'amount', 0) === null) && <AddAnEmergencyFund />}

                <SellCompanyStock />

                <Page.ButtonBox>
                  <Button
                    type='button'
                    onClick={handleSubmit}
                    primary
                    label='Recalculate'
                    width='140px'
                    css={css`
                      height: 45px;
                      align-self: 'right';
                      margin-top: 30px;
                    `}
                  />
                </Page.ButtonBox>
              </div>

              <Page.ReturnText>
                Return to Overall Strategy to review Social Security, pension and other lifetime
                income selections, or click "income needs & wants" to review your budget
              </Page.ReturnText>
            </div>
          </Page.BorderedCard>

          <Page.ButtonRow>
            <Condition when='page' is={1}>
              <BackButton
                css={css`
                  margin-top: 40px;
                `}
                backgroundColor='#FFFFFF'
                onClick={() => navigate(-1)}>
                Cancel
              </BackButton>
            </Condition>
            <Button
              type='button'
              onClick={handleSubmit}
              secondary
              label='No changes to variables'
              width='245px'
              css={css`
                height: 45px;
                align-self: flex-start;
                margin-top: 40px;
                background-color: #4a606a9c;
                color: #fff;
              `}
            />
          </Page.ButtonRow>
        </Page.Container>
      )}
    />
  )
}

export default withRouterProps(inject('store', 'main')(observer(FinalizeChoices)))

const ChangeLifeExpectancyAge = ({ person, spouse, spendingRecommendedCases }) => {
  const [open, setOpen] = React.useState(false)
  const theme = useTheme()
  return (
    <Page.ConsiderationWrapper>
      <Page.ConsiderationContainer onClick={() => setOpen(!open)}>
        <SVGWrapper
          css={css`
            margin-right: 10px;
          `}
          svg={open ? CircleNegative : CirclePlus}
          fill={theme.circleIconColor} // '#3A4D57'
          size='large'
        />
        <Page.ConsiderationLabel>Change life expectancy age</Page.ConsiderationLabel>
      </Page.ConsiderationContainer>
      {open && (
        <Page.ConsiderationBody>
          {person.includeSpouse ? (
            <Page.Note>
              Please note: GuidedChoice plans for 10 years beyond the latest life expectancy age to
              help prevent running out of money.
            </Page.Note>
          ) : (
            <Page.Note>
              Please note: GuidedChoice plans for 10 years beyond the life expectancy age to help
              prevent running out of money.
            </Page.Note>
          )}
          <Field
            name='clientLifeExpectancy'
            subscription={{ value: true, touched: true, error: true }}
            render={({ input }) => (
              <Page.SliderContainer>
                <Page.SliderLabel>{person.displayName}</Page.SliderLabel>
                <Page.SliderValue>
                  <SliderWithValueUnderneath
                    min={person.retirementAge + ''}
                    max={person.age + 85 > 120 ? '120' : (person.age + 85).toString()}
                    value={input.value}
                    onChange={input.onChange}
                    frozenValue={_.get(
                      spendingRecommendedCases,
                      'averageMarketSpendingAdvice.primary.lifeExpectancyAge',
                      null
                    )}
                  />
                </Page.SliderValue>
              </Page.SliderContainer>
            )}
          />
          {!person.includeSpouse && <Spacer space='12px' />}
          {person.includeSpouse && (
            <div
              css={css`
                margin: 25px 0;
              `}>
              <Spacer space='12px;' />
              <Field
                name='spouseLifeExpectancy'
                subscription={{ value: true, touched: true, error: true }}
                render={({ input }) => (
                  <Page.SliderContainer>
                    <Page.SliderLabel>{spouse.firstName}</Page.SliderLabel>
                    <Page.SliderValue>
                      <SliderWithValueUnderneath
                        min={spouse.retirementAge + ''}
                        max={spouse.age + 85 > 120 ? '120' : (spouse.age + 85).toString()}
                        value={input.value}
                        onChange={input.onChange}
                        frozenValue={_.get(
                          spendingRecommendedCases,
                          'averageMarketSpendingAdvice.spouse.lifeExpectancyAge',
                          null
                        )}
                      />
                    </Page.SliderValue>
                  </Page.SliderContainer>
                )}
              />
            </div>
          )}
        </Page.ConsiderationBody>
      )}
    </Page.ConsiderationWrapper>
  )
}
